import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import type * as React from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from '../lib/auth/auth-context';
import { supabase as defaultClient } from '../lib/supabase';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { Alert } from '../components/ui/Alert';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';
import { RouteMap } from '../components/routes/RouteMap';

// Same UUID-shape regex pattern as the order detail route. Kept local
// (rather than imported from the orders module) so the routes surface
// can evolve its own validator without coupling to orders.
export const ROUTE_ID_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isRouteIdParam(value: unknown): value is string {
  return typeof value === 'string' && ROUTE_ID_UUID_RE.test(value);
}

// Route param is the route UUID (routes.id). Decision 8 applies —
// no human-readable identifier in the URL. parseParams rejects
// non-UUID segments via notFound() before mount so /routes/not-a-uuid
// renders the same UI as /routes/<unknown-uuid>.
export const Route = createFileRoute('/_authenticated/routes/$routeId')({
  component: RouteDetailPage,
  notFoundComponent: RouteNotFoundRoute,
  parseParams: (params): { routeId: string } => {
    if (!isRouteIdParam(params.routeId)) {
      throw notFound();
    }
    return { routeId: params.routeId };
  },
});

type StopRow = {
  id: string;
  sequence_number: number | null;
  stop_type: string;
  status: string;
  address: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  // numeric(10,7) columns; PostgREST serializes numeric as string by
  // default. Either field NULL → "Geocoding pending" rendering.
  latitude: string | null;
  longitude: string | null;
  orders: {
    id: string;
    order_number: string;
    patient_name: string | null;
    status: string;
  }[];
};

type RouteRow = {
  id: string;
  route_status: string;
  planned_for: string;
  created_at: string;
  tenant_id: string;
  driver_id: string | null;
};

// Active-driver option shown in the assignment dropdown. The dropdown
// only renders drivers with drivers.status='active' (filtered server-
// side via the !inner join), so the option label safely reads
// employee_id + full_name with no status disambiguation.
type ActiveDriverOption = {
  id: string;
  full_name: string | null;
  employee_id: string | null;
};

type AuditRow = {
  id: string;
  occurred_at: string;
  event_type: string;
  actor_user_id: string | null;
  actor_type: string;
  payload: unknown;
  actor_user: { full_name: string | null; email: string } | null;
};

type DetailState =
  | { kind: 'loading' }
  | {
      kind: 'loaded';
      route: RouteRow;
      stops: StopRow[];
      audit: AuditRow[];
      drivers: ActiveDriverOption[];
    }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatPlannedFor(iso: string): string {
  // routes.planned_for is a Postgres date (YYYY-MM-DD). Parse as local
  // calendar date rather than as UTC midnight to avoid off-by-one in
  // negative timezones.
  const parts = iso.split('-');
  if (parts.length === 3) {
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      const local = new Date(y, m - 1, d);
      return local.toLocaleDateString(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  }
  return iso;
}

function actorDisplay(row: AuditRow): string {
  if (row.actor_user?.full_name) return row.actor_user.full_name;
  if (row.actor_user?.email) return row.actor_user.email;
  if (row.actor_user_id) return row.actor_user_id;
  return row.actor_type;
}

// Label rendered for a driver row in the assignment dropdown. Mirrors
// the convention on the drivers list page: "EMP-ID · Full Name" when
// an employee_id is set, plain full_name otherwise. Falls back to the
// short user id if full_name is also missing — drivers without a
// name shouldn't reach this surface (create_driver requires it), but
// defensive rendering beats a blank <option>.
function driverOptionLabel(d: ActiveDriverOption): string {
  const name = d.full_name?.trim() || d.id.slice(0, 8);
  if (d.employee_id && d.employee_id.trim().length > 0) {
    return `${d.employee_id} · ${name}`;
  }
  return name;
}

// LYNRX_* error prefixes raised by assign_route_driver. The RPC writes
// these messages verbatim; the UI matches on prefix (not full text) so
// the SQL side can amend the trailing detail without breaking the map.
function friendlyAssignError(message: string | undefined): string {
  const m = message ?? '';
  if (m.includes('LYNRX_ROUTE_NOT_FOUND'))
    return 'This route is no longer accessible. Refresh.';
  if (m.includes('LYNRX_DRIVER_NOT_FOUND'))
    return "Selected driver isn't available. Refresh.";
  if (m.includes('LYNRX_DRIVER_NOT_ACTIVE'))
    return "That driver isn't active anymore.";
  return 'Failed to assign driver. Try again.';
}

// LYNRX_* error prefixes raised by publish_route / unpublish_route. Same
// prefix-match convention as the assign mapper above so the SQL side
// can amend trailing detail without breaking the UI.
function friendlyPublishError(message: string | undefined): string {
  const m = message ?? '';
  if (m.includes('LYNRX_ROUTE_NOT_FOUND'))
    return 'Route no longer accessible. Refresh.';
  if (m.includes('LYNRX_ROUTE_NO_STOPS'))
    return 'Add at least one stop before publishing.';
  if (m.includes('LYNRX_ROUTE_ALREADY_PUBLISHED'))
    return 'Route is already published. Refresh to see the latest state.';
  if (m.includes('LYNRX_ROUTE_NOT_PUBLISHED'))
    return 'Route is not currently published.';
  return 'Action failed. Try again.';
}

// Renders a status hint for a stop's geocoding state. Only surfaces
// when there's a problem (missing/invalid coords) — happy-path
// dispatchers don't need lat/lng noise on every card; the map has
// them visually. Returns null for the happy path so the card can
// omit the row entirely.
function coordinatesLine(stop: StopRow): JSX.Element | null {
  if (stop.latitude === null || stop.longitude === null) {
    return <span className="italic text-ink-3">Geocoding pending</span>;
  }
  const lat = Number(stop.latitude);
  const lng = Number(stop.longitude);
  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return <span className="italic text-ink-3">Geocoding pending</span>;
  }
  return null;
}

function addressLine(stop: StopRow): string {
  const parts: string[] = [stop.address];
  const cityState = [stop.city, stop.state].filter(Boolean).join(', ');
  if (cityState) parts.push(cityState);
  if (stop.zip) parts.push(stop.zip);
  return parts.join(' · ');
}

// Recipient label for a stop. Pickups don't have a recipient (the
// package is at the pharmacy); dropoffs read the patient_name from
// the first joined order. Consolidated dropoffs (>1 order) get a
// "<first> + N more" suffix so the card stays compact.
//
// Returns a JSX node rather than a string so the "no name on file"
// fallback can render italic/gray — "Unknown" (the old fallback)
// read like a data-quality bug more than an empty state.
function recipientLabel(stop: StopRow): JSX.Element {
  if (stop.stop_type === 'pickup') {
    return <>Pharmacy pickup</>;
  }
  const orders = stop.orders;
  if (orders.length === 0) return <>—</>;
  const rawFirst = orders[0]?.patient_name;
  const suffix =
    orders.length > 1 ? ` + ${orders.length - 1} more` : '';
  if (!rawFirst) {
    return (
      <>
        <span className="italic text-ink-2">No name on file</span>
        {suffix}
      </>
    );
  }
  return (
    <>
      {rawFirst}
      {suffix}
    </>
  );
}

function totalOrderCount(stops: StopRow[]): number {
  let n = 0;
  for (const stop of stops) {
    // Only dropoff stops bring orders — pickup stops link back to the
    // same orders via pickup_stop_id in a future iteration, but for
    // the count-the-orders display we don't want to double-count.
    if (stop.stop_type === 'dropoff') n += stop.orders.length;
  }
  return n;
}

function RouteDetailPage(): JSX.Element {
  const { routeId } = Route.useParams();
  const navigate = useNavigate();
  // "Back" from this page goes to the routes list — matches the shell
  // breadcrumb (Routes /) and is where users expect a route's parent
  // to be. Formerly pointed at `/orders?status=pending`, which lied
  // when the user arrived from anywhere other than that queue.
  // Search fields mirror Sidebar's Routes entry (date/status/driver);
  // validateSearch normalizes 'today' → todayIso() at the target.
  const goBack = useCallback(() => {
    void navigate({
      to: '/routes',
      search: { date: 'today', status: 'all', driver: 'all' },
    });
  }, [navigate]);
  return <RouteDetailView routeId={routeId} onBack={goBack} />;
}

type ViewProps = {
  routeId: string;
  onBack: () => void;
  client?: LynrxSupabaseClient;
};

export function RouteDetailView({
  routeId,
  onBack,
  client = defaultClient,
}: ViewProps): JSX.Element {
  const { session, isReady } = useAuth();
  const [state, setState] = useState<DetailState>({ kind: 'loading' });
  // Register the shell's top-bar title. Generic placeholder while
  // loading; once the route lands the effect refires with the
  // human-friendly "Date · short-id" label.
  const headerTitle =
    state.kind === 'loaded'
      ? `${formatPlannedFor(state.route.planned_for)} · ${state.route.id.slice(0, 8)}`
      : 'Route';
  useSetPageHeader({
    title: headerTitle,
    parentLabel: 'Routes',
    parentTo: '/routes',
  });

  useEffect(() => {
    if (!isReady || !session) return;
    let cancelled = false;
    setState({ kind: 'loading' });

    void (async () => {
      // RLS scopes all three queries to the caller's tenant + pharmacy.
      // A cross-tenant routeId returns no row (404 UI); a real DB
      // error becomes the error state.
      const routeQuery = client
        .from('routes')
        .select('id, route_status, planned_for, created_at, tenant_id, driver_id')
        .eq('id', routeId)
        .is('deleted_at', null)
        .maybeSingle();

      const stopsQuery = client
        .from('stops')
        .select(
          'id, sequence_number, stop_type, status, address, city, state, zip, latitude, longitude, orders!orders_dropoff_stop_id_fkey(id, order_number, patient_name, status)',
        )
        .eq('route_id', routeId)
        .order('sequence_number', { ascending: true });

      const auditQuery = client
        .from('audit_events')
        .select(
          'id, occurred_at, event_type, actor_user_id, actor_type, payload, actor_user:users!audit_events_actor_user_id_fkey(full_name, email)',
        )
        .eq('entity_type', 'route')
        .eq('entity_id', routeId)
        .order('occurred_at', { ascending: true });

      const [routeResult, stopsResult, auditResult] = await Promise.all([
        routeQuery,
        stopsQuery,
        auditQuery,
      ]);

      if (cancelled) return;

      if (routeResult.error) {
        Sentry.captureException(routeResult.error, {
          tags: { area: 'routes.detail.fetch_route' },
        });
        setState({ kind: 'error', message: 'Could not load this route.' });
        return;
      }
      if (!routeResult.data) {
        setState({ kind: 'not_found' });
        return;
      }

      // Stops/audit failures are non-fatal — the route still renders;
      // we degrade the corresponding section.
      const stops = stopsResult.error
        ? []
        : ((stopsResult.data ?? []) as unknown as StopRow[]);
      if (stopsResult.error) {
        Sentry.captureException(stopsResult.error, {
          tags: { area: 'routes.detail.fetch_stops' },
        });
      }
      const audit = auditResult.error
        ? []
        : ((auditResult.data ?? []) as unknown as AuditRow[]);
      if (auditResult.error) {
        Sentry.captureException(auditResult.error, {
          tags: { area: 'routes.detail.fetch_audit' },
        });
      }

      // Active drivers in this tenant — populates the assignment
      // dropdown. !inner on the drivers join lets PostgREST filter on
      // drivers.status server-side; RLS scopes to the caller's tenant
      // automatically (drivers RLS — pharmacy_access overlap or
      // tenant_wide). Failure here is non-fatal: the dropdown
      // degrades to "Unassigned only" and we Sentry the error.
      const driversResult = await client
        .from('users')
        .select(
          'id, full_name, employee_id, drivers:drivers!drivers_id_fkey!inner(status)',
        )
        .eq('user_type', 'driver')
        .eq('drivers.status', 'active')
        .is('deleted_at', null)
        .order('full_name', { ascending: true });

      if (cancelled) return;

      const drivers = driversResult.error
        ? []
        : ((driversResult.data ?? []) as unknown as ActiveDriverOption[]);
      if (driversResult.error) {
        Sentry.captureException(driversResult.error, {
          tags: { area: 'routes.detail.fetch_drivers' },
        });
      }

      setState({
        kind: 'loaded',
        route: routeResult.data as unknown as RouteRow,
        stops,
        audit,
        drivers,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [client, session, isReady, routeId]);

  return (
    <div className="mx-auto max-w-3xl lg:max-w-none">
      {/* No inline back link — the shell's top-bar breadcrumb
          (Routes /) is the single navigation affordance to the parent.
          RouteNotFoundCard still has its own button for the case where
          no header context is meaningful. */}
      {state.kind === 'loading' ? (
        <Card>
          <p className="text-sm text-ink-2">Loading route…</p>
        </Card>
      ) : null}

      {state.kind === 'not_found' ? <RouteNotFoundCard onBack={onBack} /> : null}

      {state.kind === 'error' ? (
        <Alert tone="error" title="Something went wrong">
          {state.message}
        </Alert>
      ) : null}

      {state.kind === 'loaded' ? (
        <LoadedView
          route={state.route}
          stops={state.stops}
          audit={state.audit}
          drivers={state.drivers}
          client={client}
        />
      ) : null}
    </div>
  );
}

function LoadedView({
  route,
  stops,
  audit,
  drivers,
  client,
}: {
  route: RouteRow;
  stops: StopRow[];
  audit: AuditRow[];
  drivers: ActiveDriverOption[];
  client: LynrxSupabaseClient;
}): JSX.Element {
  const idPrefix = route.id.slice(0, 8);
  // Lift route_status into local state so publish_route /
  // unpublish_route can update the badge, banner, and control
  // optimistically without refetching the route row. Same pattern as
  // DriverAssignSelect lifting selectedId from currentDriverId.
  const [currentStatus, setCurrentStatus] = useState<string>(route.route_status);
  useEffect(() => {
    setCurrentStatus(route.route_status);
  }, [route.route_status]);
  const isDraft = currentStatus === 'draft';
  const orderCount = totalOrderCount(stops);

  // Stop ↔ pin selection. Single source of truth shared by the stop
  // list and the map: a card click or a pin click sets it, and it flows
  // back to both children (card highlight + pin focus). Toggling the
  // already-selected stop clears it. Ephemeral single-page UI state, so
  // plain useState here rather than Context/Zustand.
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);
  const handleSelectStop = useCallback((id: string) => {
    setSelectedStopId((cur) => (cur === id ? null : id));
  }, []);

  // Full-canvas view toggle (Stops list vs Map). Ephemeral single-
  // page UI state — a router-backed variant with `?view=map` would
  // survive refreshes but needs a memory-router in tests that this
  // codebase doesn't yet set up; revisit alongside broader router-
  // aware test infrastructure.
  const [view, setView] = useState<'list' | 'map'>('list');

  // Overlay search filter for the map view's stops key panel. Filters
  // by address substring + patient name substring. Empty string = show
  // all stops. Ephemeral, panel-scoped.
  const [stopFilter, setStopFilter] = useState('');
  const filteredStops = stopFilter.trim()
    ? stops.filter((stop) => {
        const needle = stopFilter.trim().toLowerCase();
        if (stop.address.toLowerCase().includes(needle)) return true;
        return stop.orders.some((o) =>
          (o.patient_name ?? '').toLowerCase().includes(needle),
        );
      })
    : stops;

  return (
    <>
      {/* In-page route metadata. Rendered only in list view — map view
          ceded the vertical space to the map itself (CNC pattern). The
          route's identity (date + short id) still lives in the shell's
          top bar so the map view isn't context-free. Dispatchers who
          need to reassign a driver or publish flip to the list. */}
      {view === 'list' ? (
        <header className="mb-3 rounded-md border border-border bg-bg px-4 py-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-ink-2">
              Created {formatDateTime(route.created_at)}
            </p>
            <div className="flex items-center gap-3">
              {isDraft ? (
                // Builder entry point. Visible only while the route is
                // draft — published/in_progress/completed routes are not
                // structurally editable. Same secondary-button styling
                // as the unpublish control beside it (see PR #55).
                <Link
                  to="/routes/$routeId/build"
                  params={{ routeId: route.id }}
                  className="inline-flex items-center justify-center rounded-md border border-border bg-bg px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-bg-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  Build / edit stops
                </Link>
              ) : null}
              <DriverAssignSelect
                routeId={route.id}
                currentDriverId={route.driver_id}
                drivers={drivers}
                client={client}
              />
              <PublishRouteControl
                routeId={route.id}
                currentStatus={currentStatus}
                onStatusChange={setCurrentStatus}
                client={client}
              />
              <StatusBadge entity="route" status={currentStatus} />
            </div>
          </div>
          <dl className="mt-2 grid grid-cols-3 gap-x-6 gap-y-1 text-sm">
            <div>
              <dt className="text-xs text-ink-2">ID</dt>
              <dd className="font-mono text-ink">{idPrefix}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-2">Stops</dt>
              <dd className="text-ink">{stops.length}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-2">Orders</dt>
              <dd className="text-ink">{orderCount}</dd>
            </div>
          </dl>
        </header>
      ) : null}

      {isDraft ? (
        <div className="mb-3 rounded-md border border-border bg-bg px-4 py-2 text-sm text-ink-2">
          This route is in draft. Drivers cannot see it until published.
        </div>
      ) : null}

      {/* Toolbar row — segmented "Stops | Map" toggle right-aligned.
          Parent nav is the shell's Routes / breadcrumb, so no inline
          back link here. Mirrors CNC's dispatch view: one compact
          strip above a full-canvas map. */}
      <div className="mb-2 flex items-center justify-end">
        <div
          role="tablist"
          aria-label="View"
          className="inline-flex rounded-md border border-border bg-bg p-0.5"
        >
          <button
            type="button"
            role="tab"
            aria-selected={view === 'list'}
            onClick={() => setView('list')}
            className={`rounded-sm px-3 py-1.5 text-sm transition-colors ${
              view === 'list'
                ? 'bg-bg-soft font-medium text-ink'
                : 'text-ink-2 hover:text-ink'
            }`}
          >
            Stops
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'map'}
            onClick={() => setView('map')}
            className={`rounded-sm px-3 py-1.5 text-sm transition-colors ${
              view === 'map'
                ? 'bg-bg-soft font-medium text-ink'
                : 'text-ink-2 hover:text-ink'
            }`}
          >
            Map
          </button>
        </div>
      </div>

      {view === 'list' ? (
        <>
          <section className="mb-4">
            {stops.length === 0 ? (
              <Card>
                <p className="text-sm text-ink-2">
                  This route has no stops. Add orders from the pending queue to
                  build the route.
                </p>
              </Card>
            ) : (
              <ol className="space-y-2">
                {stops.map((stop) => (
                  <StopCard
                    key={stop.id}
                    stop={stop}
                    isSelected={stop.id === selectedStopId}
                    onSelect={handleSelectStop}
                  />
                ))}
              </ol>
            )}
          </section>

          {/* Activity collapsed by default — most dispatchers don't need
              to read the audit log on a normal load. <details> is the
              stdlib disclosure widget; no JS needed for the toggle.
              `group` + `group-open:rotate-90` on the chevron gives a
              CSS-only affordance for the open state. `list-none` +
              WebKit marker override hide the default triangle so our
              chevron is the only expand indicator. */}
          <details className="group rounded-md border border-border bg-bg">
            <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-bg-soft [&::-webkit-details-marker]:hidden">
              <svg
                aria-hidden="true"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0 text-ink-2 transition-transform group-open:rotate-90"
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
              <span>Activity</span>
              <span className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-bg-soft px-1.5 py-0.5 text-[11px] font-medium text-ink-2">
                {audit.length}
              </span>
            </summary>
            <div className="border-t border-border px-4 py-3">
              {audit.length === 0 ? (
                <p className="text-sm text-ink-2">No events for this route yet.</p>
              ) : (
                <ol className="space-y-3 text-sm">
                  {audit.map((row) => (
                    <AuditEntry key={row.id} row={row} />
                  ))}
                </ol>
              )}
            </div>
          </details>
        </>
      ) : (
        <div className="relative h-[500px] overflow-hidden rounded-md border border-border bg-bg lg:h-[calc(100vh-7rem)]">
          <RouteMap
            stops={stops}
            selectedStopId={selectedStopId}
            onSelectStop={handleSelectStop}
          />
          {/* Stops key overlay — top-left, CNC-inspired. Search filters
              the list; clicking a stop drives the same selectedStopId
              state the map already reads, so pin focus is free. Pointer-
              events wrapper isolates map drags: only the panel captures
              clicks; the surrounding transparent area stays draggable. */}
          <div className="pointer-events-none absolute inset-0">
            <div className="pointer-events-auto absolute left-4 top-4 flex max-h-[min(420px,calc(100%-2rem))] w-[224px] flex-col rounded-lg border border-border bg-white/90 shadow-md ring-1 ring-black/5 backdrop-blur-md">
              <div className="border-b border-border px-2.5 py-1.5">
                <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-ink-2">
                  Stops ({stops.length})
                </div>
                <input
                  type="search"
                  placeholder="Search stops…"
                  value={stopFilter}
                  onChange={(e) => setStopFilter(e.target.value)}
                  className="w-full rounded-sm border border-border bg-bg px-2 py-0.5 text-xs text-ink placeholder:text-ink-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <ol className="flex-1 overflow-y-auto py-0.5">
                {filteredStops.length === 0 ? (
                  <li className="px-2.5 py-1.5 text-xs text-ink-2">
                    No stops match “{stopFilter}”.
                  </li>
                ) : (
                  filteredStops.map((stop) => {
                    const isPickup = stop.stop_type === 'pickup';
                    const isSelected = stop.id === selectedStopId;
                    const badgeLabel = isPickup
                      ? 'P'
                      : (stop.sequence_number ?? '?').toString();
                    // Primary line: address if we have it; otherwise a
                    // placeholder so the row is still meaningful.
                    const primary = stop.address || 'Address unavailable';
                    // Secondary line: patient name(s) for dropoffs.
                    // Multi-order stops render "Name +N more"; empty
                    // for pickup rows.
                    // Destructure rather than index: strict mode's
                    // noUncheckedIndexedAccess types patients[0] as
                    // string | undefined even after a length check.
                    const [firstPatient, ...restPatients] = stop.orders
                      .map((o) => o.patient_name)
                      .filter((n): n is string => !!n);
                    const secondary = !firstPatient
                      ? null
                      : restPatients.length === 0
                        ? firstPatient
                        : `${firstPatient} +${restPatients.length} more`;
                    return (
                      <li key={stop.id}>
                        <button
                          type="button"
                          onClick={() => handleSelectStop(stop.id)}
                          className={`flex w-full items-center gap-2 px-2.5 py-1 text-left text-xs transition-colors hover:bg-bg-soft ${
                            isSelected ? 'bg-bg-soft' : ''
                          }`}
                        >
                          <span
                            className={`inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-[9px] font-semibold text-white ${
                              isPickup ? 'bg-primary' : 'bg-ink'
                            }`}
                          >
                            {badgeLabel}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span
                              className={`block truncate text-ink ${
                                isSelected ? 'font-medium' : ''
                              }`}
                            >
                              {primary}
                            </span>
                            {secondary ? (
                              <span className="block truncate text-[10px] text-ink-2">
                                {secondary}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    );
                  })
                )}
              </ol>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Driver assignment dropdown. Lives in the route detail header next to
// the status badge. The "— Unassigned —" option (value="") maps to
// p_driver_id IS NULL in the assign_route_driver RPC. Optimistic
// update: we set local selectedId before the round-trip, revert and
// surface an inline error if the RPC raises. Auto-clearing success
// message gives the dispatcher a moment to confirm the change without
// pulling in a toast library.
function DriverAssignSelect({
  routeId,
  currentDriverId,
  drivers,
  client,
}: {
  routeId: string;
  currentDriverId: string | null;
  drivers: ActiveDriverOption[];
  client: LynrxSupabaseClient;
}): JSX.Element {
  const [selectedId, setSelectedId] = useState<string | null>(currentDriverId);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<
    { tone: 'success' | 'error'; text: string } | null
  >(null);

  // Keep the local selection in sync if the parent rehydrates the
  // route row (e.g., after a future refresh). Without this, an
  // out-of-band change would never propagate to the controlled select.
  useEffect(() => {
    setSelectedId(currentDriverId);
  }, [currentDriverId]);

  // Auto-clear the feedback message after a few seconds so it reads
  // like a toast without the dependency. Errors and successes both
  // dismiss on the same cadence; the dispatcher can re-trigger by
  // changing the selection again.
  useEffect(() => {
    if (!feedback) return;
    const handle = window.setTimeout(() => setFeedback(null), 3000);
    return () => window.clearTimeout(handle);
  }, [feedback]);

  // If the currently-assigned driver was deactivated between page load
  // and now, it won't appear in `drivers`. Render it as a synthetic
  // option so the select isn't forced into a different value just by
  // mounting — the user gets a deliberate "switch away" action instead.
  const knownDriverIds = new Set(drivers.map((d) => d.id));
  const showOrphanedOption =
    selectedId !== null && !knownDriverIds.has(selectedId);

  const onChange = useCallback(
    async (e: React.ChangeEvent<HTMLSelectElement>) => {
      const raw = e.target.value;
      const next: string | null = raw === '' ? null : raw;
      const previous = selectedId;
      // Optimistic update — revert on RPC error.
      setSelectedId(next);
      setSubmitting(true);
      setFeedback(null);
      try {
        const { error } = await client.rpc('assign_route_driver', {
          p_route_id: routeId,
          // Regen drift after PR #72's types regen: assign_route_driver
          // accepts null at runtime to unassign the driver (the
          // "Unassign" UI path uses raw='' → null), but the regenerated
          // Args type dropped the `| null` annotation. Cast preserves
          // the unassign path; remove when the regen recovers it.
          p_driver_id: next as string,
        });
        if (error) {
          setSelectedId(previous);
          Sentry.captureException(error, {
            tags: { area: 'routes.detail.assign_driver' },
          });
          setFeedback({
            tone: 'error',
            text: friendlyAssignError(error.message),
          });
          return;
        }
        setFeedback({
          tone: 'success',
          text: next === null ? 'Driver unassigned.' : 'Driver assigned.',
        });
      } catch (err) {
        setSelectedId(previous);
        Sentry.captureException(err, {
          tags: { area: 'routes.detail.assign_driver.exception' },
        });
        setFeedback({
          tone: 'error',
          text: friendlyAssignError(undefined),
        });
      } finally {
        setSubmitting(false);
      }
    },
    [client, routeId, selectedId],
  );

  return (
    <div className="flex flex-col items-end gap-1">
      <label className="flex items-center gap-2 text-sm">
        <span className="text-xs text-ink-2">Driver</span>
        <select
          value={selectedId ?? ''}
          onChange={onChange}
          disabled={submitting}
          aria-label="Assign driver to route"
          className="rounded-md border border-border bg-bg px-3 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
        >
          <option value="">— Unassigned —</option>
          {showOrphanedOption ? (
            <option value={selectedId ?? ''}>
              {/* Current value isn't an active driver anymore. Surface
                  the short id so the dispatcher knows what's currently
                  bound; the only other path is "switch to active". */}
              {selectedId ? selectedId.slice(0, 8) : ''} (inactive)
            </option>
          ) : null}
          {drivers.map((d) => (
            <option key={d.id} value={d.id}>
              {driverOptionLabel(d)}
            </option>
          ))}
        </select>
      </label>
      {feedback ? (
        <p
          role="status"
          className={
            feedback.tone === 'success'
              ? 'text-xs text-emerald-700'
              : 'text-xs text-red-700'
          }
        >
          {feedback.text}
        </p>
      ) : null}
    </div>
  );
}

// Publish / unpublish control. Lives next to the driver dropdown in
// the route detail header. Renders nothing for terminal statuses
// (in_progress, completed, cancelled) — those are downstream of the
// driver app and aren't reachable from the dispatcher UI yet, but the
// defensive empty render keeps the control from appearing on a
// future route that's already moved past the dispatcher's control.
//
// Optimistic update: bumps the parent's currentStatus before the RPC
// returns, reverts on error. Same shape as DriverAssignSelect; the
// state lifted into LoadedView drives the status badge + draft banner
// rerender in lockstep.
function PublishRouteControl({
  routeId,
  currentStatus,
  onStatusChange,
  client,
}: {
  routeId: string;
  currentStatus: string;
  onStatusChange: (next: string) => void;
  client: LynrxSupabaseClient;
}): JSX.Element | null {
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<
    { tone: 'success' | 'error'; text: string } | null
  >(null);

  // Auto-clear feedback after a few seconds so the row reads like a
  // toast without pulling in a toast library — same cadence as the
  // driver dropdown above.
  useEffect(() => {
    if (!feedback) return;
    const handle = window.setTimeout(() => setFeedback(null), 3000);
    return () => window.clearTimeout(handle);
  }, [feedback]);

  const runTransition = useCallback(
    async (rpcName: 'publish_route' | 'unpublish_route', nextStatus: string) => {
      const previous = currentStatus;
      onStatusChange(nextStatus);
      setSubmitting(true);
      setFeedback(null);
      try {
        const { error } = await client.rpc(rpcName, { p_route_id: routeId });
        if (error) {
          onStatusChange(previous);
          Sentry.captureException(error, {
            tags: { area: `routes.detail.${rpcName}` },
          });
          setFeedback({
            tone: 'error',
            text: friendlyPublishError(error.message),
          });
          return;
        }
        setFeedback({
          tone: 'success',
          text:
            rpcName === 'publish_route'
              ? 'Route published. Visible to assigned driver.'
              : 'Route unpublished.',
        });

        // On successful publish, fire the push notification to the
        // assigned driver's phone. Fire-and-forget — endpoint returns
        // {sent: false, reason: '...'} for driverless routes /
        // no-token / Expo hiccups, and we don't want any of that to
        // pollute the dispatcher's "published successfully" toast.
        if (rpcName === 'publish_route') {
          try {
            const { data: sess } = await client.auth.getSession();
            const token = sess.session?.access_token;
            if (token) {
              await fetch('/api/notifications/route-published', {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ route_id: routeId }),
              });
            }
          } catch (pushErr) {
            Sentry.captureException(pushErr, {
              tags: { area: 'routes.detail.publish.notify' },
            });
          }
        }
      } catch (err) {
        onStatusChange(previous);
        Sentry.captureException(err, {
          tags: { area: `routes.detail.${rpcName}.exception` },
        });
        setFeedback({
          tone: 'error',
          text: friendlyPublishError(undefined),
        });
      } finally {
        setSubmitting(false);
      }
    },
    [client, currentStatus, onStatusChange, routeId],
  );

  const onPublish = useCallback(
    () => void runTransition('publish_route', 'published'),
    [runTransition],
  );
  const onUnpublish = useCallback(
    () => void runTransition('unpublish_route', 'draft'),
    [runTransition],
  );

  if (currentStatus !== 'draft' && currentStatus !== 'published') {
    return null;
  }

  return (
    <div className="flex flex-col items-end gap-1">
      {currentStatus === 'draft' ? (
        // Emerald-styled action button. The Button component's primary
        // variant is the brand teal; publish is its own distinct,
        // affirmative action so we use emerald directly here (same
        // tone as the success Alert and the dropdown feedback text).
        <button
          type="button"
          onClick={onPublish}
          disabled={submitting}
          className="inline-flex items-center justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Publishing…' : 'Publish route'}
        </button>
      ) : (
        <Button variant="secondary" onClick={onUnpublish} disabled={submitting}>
          {submitting ? 'Unpublishing…' : 'Unpublish'}
        </Button>
      )}
      {feedback ? (
        <p
          role="status"
          className={
            feedback.tone === 'success'
              ? 'text-xs text-emerald-700'
              : 'text-xs text-red-700'
          }
        >
          {feedback.text}
        </p>
      ) : null}
    </div>
  );
}

// True when the stop carries usable coordinates — i.e. it has a pin on
// the map. Mirrors RouteMap's parseStops guard so the card's selection
// affordance matches whether a pin actually exists to focus.
function stopHasCoords(stop: StopRow): boolean {
  if (stop.latitude === null || stop.longitude === null) return false;
  return (
    !Number.isNaN(Number(stop.latitude)) &&
    !Number.isNaN(Number(stop.longitude))
  );
}

// Stop card. Sequence number is the prominent leading column; the
// stop_type, address, recipient summary, order-count line, and
// per-stop status badge all hang off it. Cards with coordinates are
// selectable: clicking (or Enter/Space) focuses the matching pin on the
// map, and a pin click flows back to highlight + scroll the card into
// view (selection state is owned by LoadedView). Cards without
// coordinates have no pin to focus, so they render non-interactive.
function StopCard({
  stop,
  isSelected,
  onSelect,
}: {
  stop: StopRow;
  isSelected: boolean;
  onSelect: (id: string) => void;
}): JSX.Element {
  const isDropoff = stop.stop_type === 'dropoff';
  const orderCount = stop.orders.length;
  const selectable = stopHasCoords(stop);
  const liRef = useRef<HTMLLIElement | null>(null);

  // When selection arrives from a pin click, bring the card into view.
  // scrollIntoView with block:'nearest' is a no-op if already visible.
  useEffect(() => {
    if (isSelected && liRef.current) {
      liRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isSelected]);

  const activate = useCallback(() => {
    if (selectable) onSelect(stop.id);
  }, [selectable, onSelect, stop.id]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!selectable) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        activate();
      }
    },
    [selectable, activate],
  );

  // Only selectable cards get the button semantics + hover affordance.
  const interactiveProps = selectable
    ? {
        role: 'button' as const,
        tabIndex: 0,
        'aria-pressed': isSelected,
        onClick: activate,
        onKeyDown,
      }
    : {};

  // `!p-3` overrides Card's default `p-6` (24px). Stop cards are the
  // densest surface in the app — dispatchers scan dozens per route —
  // so the extra breathing room the global card padding provides just
  // costs vertical scroll.
  const cardClass = [
    '!p-3',
    selectable ? 'cursor-pointer hover:bg-bg-soft' : '',
    isSelected ? 'bg-bg-soft ring-2 ring-primary ring-offset-1' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li ref={liRef}>
      <Card className={cardClass} {...interactiveProps}>
        <div className="flex items-start gap-3">
          <div
            aria-label={`Stop ${stop.sequence_number ?? '?'}`}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-bg text-xs font-semibold text-ink"
          >
            {stop.sequence_number ?? '?'}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="rounded-md border border-border bg-bg-soft px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-2">
                  {stop.stop_type}
                </span>
                <StatusBadge entity="stop" status={stop.status} />
              </div>
            </div>
            <p className="mt-1 text-sm font-medium text-ink">
              {recipientLabel(stop)}
            </p>
            <p className="text-sm text-ink-2">{addressLine(stop)}</p>
            {coordinatesLine(stop) ? (
              <p className="text-xs">{coordinatesLine(stop)}</p>
            ) : null}
            {isDropoff && orderCount > 0 ? (
              <p className="mt-1 text-xs text-ink-2">
                {orderCount === 1 ? (
                  <FirstOrderLink stop={stop} />
                ) : (
                  <>
                    {orderCount} orders · <FirstOrderLink stop={stop} /> and{' '}
                    {orderCount - 1} more
                  </>
                )}
              </p>
            ) : null}
          </div>
        </div>
      </Card>
    </li>
  );
}

function FirstOrderLink({ stop }: { stop: StopRow }): JSX.Element {
  const first = stop.orders[0];
  if (!first) return <span>—</span>;
  return (
    <Link
      to="/orders/$orderId"
      params={{ orderId: first.id }}
      search={{ status: 'all' }}
      className="text-primary hover:underline"
      // The card is itself clickable (selects the stop). Stop the click
      // here so following the order link navigates instead of toggling
      // selection.
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      {first.order_number}
    </Link>
  );
}

export function RouteNotFoundCard({ onBack }: { onBack: () => void }): JSX.Element {
  return (
    <Alert tone="error" title="Route not found">
      We couldn&apos;t find a route with that ID.
      <div className="mt-3">
        <Button variant="secondary" onClick={onBack}>
          Back to routes
        </Button>
      </div>
    </Alert>
  );
}

// Route-level notFoundComponent: renders when parseParams throws on a
// malformed UUID. Mirrors the orders detail page's pattern (PR #31).
function RouteNotFoundRoute(): JSX.Element {
  const navigate = useNavigate();
  const onBack = useCallback(() => {
    void navigate({ to: '/orders', search: { status: 'pending' } });
  }, [navigate]);
  return (
    <main className="min-h-screen bg-bg-soft px-6 py-12">
      <div className="mx-auto max-w-3xl">
        <div className="mb-6">
          <Link
            to="/orders"
            search={{ status: 'pending' }}
            className="text-sm text-primary hover:underline"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              onBack();
            }}
          >
            ← Back to orders
          </Link>
        </div>
        <RouteNotFoundCard onBack={onBack} />
      </div>
    </main>
  );
}

function AuditEntry({ row }: { row: AuditRow }): JSX.Element {
  const [open, setOpen] = useState(false);
  const hasPayload =
    row.payload !== null && row.payload !== undefined && row.payload !== '';
  return (
    <li className="rounded-md border border-border bg-bg-soft px-3 py-2">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <p className="font-medium text-ink">{row.event_type}</p>
          <p className="text-xs text-ink-2">
            {formatDateTime(row.occurred_at)} · {actorDisplay(row)}
          </p>
        </div>
        {hasPayload ? (
          <button
            type="button"
            aria-expanded={open}
            className="text-xs font-medium text-ink underline-offset-4 hover:underline"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Hide' : 'Show'} payload
          </button>
        ) : null}
      </div>
      {hasPayload && open ? (
        <pre className="mt-2 overflow-x-auto rounded bg-bg px-3 py-2 text-xs text-ink">
          {JSON.stringify(row.payload, null, 2)}
        </pre>
      ) : null}
    </li>
  );
}
